import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { useRef } from 'react';
import { Button, CloseButton, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import ReactSelect from 'react-select';
import { AttachmentIcon, UserProfilePictureIcon, VideoCallChatIcon, VoiceCallChatIcon } from './icons/Icons';
import styles from './VideoAndMessage.module.css';
import { setNewChat, setRecipient, setReloadMessageList } from '../views/patient-message/PatientMessageSlicer';
import ChatDisplayContainer from './ChatDisplayContainer';
import useAxios from '../services/AxiosConfig';
import { setReloadStaffMessageList, setStaffNewChat, setStaffRecipient } from '../views/care-team-message/CareTeamMessageSlicer';
import { useWebSocket } from '../services/webSocket/useWebSocket';
import { customStyles } from './AccountManagement/CustomStyles';
import { removeUnreadPatientMessageId, removeUnreadStaffMessageId } from '../slicers/sidebarSlice';

const ChatContainer = ({ view, onStartVideoCall, onStartAudioCall, handleClose, showActions = true }) => {
  const dispatch = useDispatch();
  const [message, setMessage] = useState('');

  const [attachments, setAttachments] = useState([]);
  const [userList, setUserList] = useState([]);
  const fileInputRef = useRef(null);

  const axiosConfig = useSelector((state) => state.axiosConfig);
  const httpService = useAxios(axiosConfig.envURL, dispatch);

  const userInfoState = useSelector((state) => state.userInfo);
  const userId = userInfoState.id;
  const userRole = userInfoState.roleName;

  const patientMessage = useSelector((state) => state.patientMessage);
  const staffMessage = useSelector((state) => state.careTeam);
  const chatList = view === "staffMessage" ? staffMessage.chatList : patientMessage.chatList;
  const newChat = view === "staffMessage" ? staffMessage.newChat : patientMessage.newChat;
  const recipient = view === "staffMessage" ? staffMessage.recipient : patientMessage.recipient;
  const { sendMessage, receivedMessage } = useWebSocket();

  const [errorMessages, setErrorMessages] = useState({
    recipient: false,
    message: false
  });

  const resetErrors = () => {
    setErrorMessages({
      recipient: false,
      message: false
    });
  }

  const blankMessageError = errorMessages.message && message === "" && attachments.length === 0;

  useEffect(() => {
    const getUsers = async () => {
      let api = 'enrollment/list/patient/' + userId;
      if (view === "staffMessage") {
        api = 'list/staffAdmin/' + userId;
      }
      httpService.get(api)
        .then((responseData) => {
          if (Array.isArray(responseData.data.data)) {
            let formatted = [];
            if (view === "staffMessage") {
              formatted = formatStaffUsers(responseData.data.data, userId);
            } else {
              formatted = formatPatientUsers(responseData.data.data, userId);
            }
            setUserList(formatted);
          } else {
            console.error('Invalid data format:');
          }
        })
        .catch((err) => {
          console.log(err);
        });
    };

    getUsers();
  }, [view, userRole, userId, userInfoState?.groups, httpService]);

  const formatPatientUsers = (data, userId) => {
    const userIdSet = new Set();
    let newFormattedUserList = [];
    if (data.length >= 1) {
      newFormattedUserList = data.reduce((acc, user) => {
        if (user.id !== userId && !userIdSet.has(user.id)) {
          userIdSet.add(user.id);
          let groups = [];
          let subGroups = [];
          let teams = [];
          let groupIds = [];
          let subGroupIds = [];
          let teamIds = [];
          let organizationIds = [];
          if (user.groups) {
            user.groups.forEach(group => {
              if (group.groupName && !groups.includes(group.groupName)) {
                groups.push(group.groupName);
                groupIds.push(group.groupId);
              }
              if (group.subGroupName && !subGroups.includes(group.subGroupName)) {
                subGroups.push(group.subGroupName);
                subGroupIds.push(group.subGroupId);
              }
              if (group.teamName && !teams.includes(group.teamName)) {
                teams.push(group.teamName);
                teamIds.push(group.teamId);
              }
              if (group.organizationId) {
                organizationIds.push(group.organizationId);
              }
            });
          }
          acc.push({
            ...user,
            groups: groups,
            subGroups: subGroups,
            teams: teams,
            organizationIds: organizationIds,
            groupIds: groupIds,
            subGroupIds: subGroupIds,
            teamIds: teamIds,
            fullName: user.lastName + ", " + user.firstName,
            label: user.firstName + " " + user.lastName,
            value: user.id,
            roleName: user.role.name
          });

        }
        return acc;
      }, []);
    }
    return newFormattedUserList;
  };

  const formatStaffUsers = (users, userId) => {
    return users.filter(user => user.id !== userId).map(user => {
      return {
        ...user,
        fullName: `${user.lastName}, ${user.firstName}`,
        label: `${user.firstName} ${user.lastName}`,
        value: user.id
      };
    });
  };

  const handleAttachmentChange = (e) => {
    const files = e.target.files;
    setAttachments([...attachments, ...files]);
  };

  const handleAttachmentIconClick = () => {
    fileInputRef.current.click();
  };

  const removeAttachment = (indexToRemove) => {
    setAttachments(attachments.filter((_, index) => index !== indexToRemove));
  };

  const handleRecipientChange = (selectedOption) => {
    if (view === "staffMessage") {
      dispatch(setStaffNewChat(false));
      dispatch(setStaffRecipient({ 'id': parseInt(selectedOption.id), name: selectedOption.label, role: selectedOption.roleName }));
    } else {
      dispatch(setNewChat(false));
      dispatch(setRecipient({ 'id': parseInt(selectedOption.id), name: selectedOption.label, role: selectedOption.roleName }));
    }

  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // Extract base64 part
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const send = useCallback(async () => {
    let tempError = { ...errorMessages };
    if (!recipient || Object.keys(recipient).length === 0 || recipient?.id === 0) {
      tempError.recipient = true
      setErrorMessages(tempError);
      return;
    }
    if (message === '' && attachments.length === 0) {
      tempError.message = true
      setErrorMessages(tempError);
      return;
    }
    resetErrors();
    const attachmentsData = await Promise.all(
      attachments.map(async (file) => ({
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
        data: await readFileAsBase64(file),
      }))
    );

    const messageData = {
      senderId: userId,
      recipientId: recipient?.id,
      message,
      senderName: `${userInfoState.firstName} ${userInfoState.lastName}`,
      recipientName: recipient?.name,
      messageChannelArn: chatList[0]?.channelArn || null,
      attachments: attachmentsData,
      recipientType: view === "staffMessage" ? "STAFF" : "PATIENT",
      type: userRole === 'Patient' ? "messageFromPatient" : view === "staffMessage" ? "messageFromStaff" : "messageFromPhysician",
      physicianId: userRole !== 'Patient' ? userId : recipient.id,
      patientId: userRole === 'Patient' ? userId : recipient.id,
    };

    sendMessage(messageData);
    setAttachments([]);
    setMessage('')
    //Add 1 second delay before reloading message list
    setTimeout(() => {
      if (view === "staffMessage") {
        dispatch(setReloadStaffMessageList(true));
      } else {
        dispatch(setReloadMessageList(true));
      }
    }, 1000);
  }, [sendMessage]);

  const markMessagesAsUnread = async () => {
    if (chatList && chatList.length > 0) {
      const unreadMessages = chatList.filter(message => !message.readStatus && message.recipientId === userId);
      if (unreadMessages.length > 0) {
        const params = {
          channelArn: unreadMessages[0].channelArn,
          userId: userId
        };
        try {
          await httpService.post('chime/updateReadStatus', params);
          const isStaffMessage = view === "staffMessage";
          const message = chatList.find(
            m => m.senderId === userId || m.recipientId === userId
          );
          if (message) {
            const otherId = message.senderId === userId ? message.recipientId : message.senderId;
            dispatch(
              isStaffMessage
                ? removeUnreadStaffMessageId(parseInt(otherId))
                : removeUnreadPatientMessageId(parseInt(otherId))
            );
          }
        } catch (error) {
          console.error('Error marking messages as unread:', error);
        }
      }
    }
  };



  return (
    <div className={styles.ChatMainContainer}>
      {!newChat && (<div className={styles.TopSection}>
        <div className={styles.Username}>
          <div className={`${styles.iconContainer} ${styles.icon}`}>
            <span className={styles.initials}>
              {`${recipient?.name?.split(' ')[0][0] ?? ""}${recipient?.name?.split(' ')[1][0] ?? ""}`}
            </span>
            <UserProfilePictureIcon />
          </div>
          <span className={styles.chatName}>{recipient?.name}</span>
        </div>
        {showActions &&
          <div className={styles.Actions}>
            <Button variant="light" onClick={onStartVideoCall} >
              <VideoCallChatIcon />
            </Button>
            <Button variant="light" onClick={onStartAudioCall} >
              <VoiceCallChatIcon />
            </Button>
            <CloseButton onClick={handleClose} className={styles.CloseButton} />
          </div>
        }
      </div>)}
      {newChat && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "start", width: "100%" }}>
          <div className={styles.TopSection}>

            <div className={styles.UserSelect}>
              <ReactSelect
                isClearable={true}
                placeholder='Select Recipient'
                styles={{
                  ...customStyles.popup,
                  container: (provided) => ({
                    ...provided,
                    width: '100%',
                  }),
                  control: (provided, { isDisabled }) => ({
                    ...customStyles.popup.control(provided, { isDisabled }),
                    borderColor: (errorMessages.recipient) ? '#DC3545' : '#686869',
                    '&:hover': {
                      borderColor: (errorMessages.recipient) ? '#DC3545' : '#686869',
                    }
                  }),
                }}
                isSearchable={true}
                name="color"
                options={userList}
                onChange={handleRecipientChange}
              /></div>
            <div className={styles.Actions}>
              <CloseButton onClick={handleClose} className={styles.CloseButton} />
            </div>
          </div>
          {errorMessages.recipient && (
            <div className="page-error" style={{ paddingTop: "5px" }}>
              Please select a recipient
            </div>
          )}
        </div>
      )}
      <hr style={{ height: "10px", margin: "10px 0" }}></hr>
      <ChatDisplayContainer messages={chatList} />
      <hr style={{ height: '.5px', margin: "10px 0" }}></hr>
      <div className={styles.BottomSectionContainer}>
        <div className={styles.AttachedFiles}>
          {attachments.map((file, index) => (
            <div key={index} className={styles.AttachedFile}>
              <span>{file.name}</span>
              <Button variant="link" onClick={() => removeAttachment(index)}>Remove</Button>
            </div>
          ))}
        </div>
        {blankMessageError && (
          <div className="page-error">
            Cannot send a blank message.
          </div>
        )}
        <div className={styles.BottomSectionRow}>
          <Button variant="white" onClick={handleAttachmentIconClick}>
            <AttachmentIcon />
          </Button>
          <input
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleAttachmentChange}
            ref={fileInputRef}
          />

          <Form.Control
            type="text"
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onFocus={markMessagesAsUnread}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                send();
              }
            }}
          />
          <Button
            className={styles.sendButton}
            onClick={send}
          >
            <FontAwesomeIcon icon="fas fa-paper-plane" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatContainer;
